import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useAppUi } from '../app/useAppUi';

const toNumberOrEmpty = (v) => (v === null || v === undefined ? '' : String(v));
const toDayjsOrNull = (ms) => {
  if (ms === null || ms === undefined || ms === '') return null;
  const n = Number(ms);
  if (!Number.isFinite(n)) return null;
  const d = dayjs(n);
  return d.isValid() ? d : null;
};

export function UserPremiumDialog({ open, user, onClose, onSave, saving }) {
  const { t } = useAppUi();
  const [premium, setPremium] = React.useState(Boolean(user?.premium));
  const [duration, setDuration] = React.useState(Boolean(user?.duration));
  const [usedFree, setUsedFree] = React.useState(Boolean(user?.used_free));
  const [expiredDate, setExpiredDate] = React.useState(toDayjsOrNull(user?.expired_date));
  const [monthlyAmount, setMonthlyAmount] = React.useState(toNumberOrEmpty(user?.monthly_amount));
  const [paidMsg, setPaidMsg] = React.useState(user?.paid_msg ?? '');
  const [unpaidReason, setUnpaidReason] = React.useState(user?.unpaid_reason ?? '');

  React.useEffect(() => {
    setPremium(Boolean(user?.premium));
    setDuration(Boolean(user?.duration));
    setUsedFree(Boolean(user?.used_free));
    setExpiredDate(toDayjsOrNull(user?.expired_date));
    setMonthlyAmount(toNumberOrEmpty(user?.monthly_amount));
    setPaidMsg(user?.paid_msg ?? '');
    setUnpaidReason(user?.unpaid_reason ?? '');
  }, [user]);

  const patch = React.useMemo(() => {
    const out = {
      premium,
      duration,
      used_free: usedFree,
      paid_msg: paidMsg === '' ? null : paidMsg,
      unpaid_reason: unpaidReason === '' ? null : unpaidReason,
    };
    // Store as Unix ms at local end-of-day to avoid “expires early” issues.
    // If you want exact midnight instead, change `endOf('day')` -> `startOf('day')`.
    out.expired_date = expiredDate ? expiredDate.endOf('day').valueOf() : null;
    if (monthlyAmount !== '') out.monthly_amount = Number(monthlyAmount);
    return out;
  }, [premium, duration, usedFree, expiredDate, monthlyAmount, paidMsg, unpaidReason]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('userPremium.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('userPremium.user', {
              id: user?.id ?? '',
              name: user?.name ? `${t('userPremium.nameSep')}${user.name}` : '',
            })}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              flexWrap: 'wrap',
              alignItems: 'center',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              px: 2,
              py: 1,
            }}
          >
            <FormControlLabel
              control={<Switch checked={premium} onChange={(e) => setPremium(e.target.checked)} />}
              label={t('userPremium.labelPremium')}
            />
            <FormControlLabel
              control={<Switch checked={duration} onChange={(e) => setDuration(e.target.checked)} />}
              label={t('userPremium.labelDuration')}
            />
            <FormControlLabel
              control={<Switch checked={usedFree} onChange={(e) => setUsedFree(e.target.checked)} />}
              label={t('userPremium.labelUsedFree')}
            />
          </Stack>
          <DatePicker
            label={t('userPremium.expiredDate')}
            value={expiredDate}
            onChange={(v) => setExpiredDate(v)}
            slotProps={{
              textField: {
                helperText: expiredDate
                  ? t('userPremium.helperMs', { ms: expiredDate.endOf('day').valueOf() })
                  : t('userPremium.helperEmpty'),
              },
            }}
          />
          <TextField
            label={t('userPremium.monthlyAmount')}
            value={monthlyAmount}
            onChange={(e) => setMonthlyAmount(e.target.value)}
            inputMode="numeric"
          />
          <TextField
            label={t('userPremium.paidMsg')}
            value={paidMsg}
            onChange={(e) => setPaidMsg(e.target.value)}
          />
          <TextField
            label={t('userPremium.unpaidReason')}
            value={unpaidReason}
            onChange={(e) => setUnpaidReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(patch)}
          disabled={saving}
        >
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

